import { DateTime } from 'luxon'
import dayjs from 'dayjs'

const useDate = () => {
  const getMonths = (): string[] =>
    Array.from({ length: 12 }, (_, i) => DateTime.local().set({ month: i + 1 }).monthLong)

  const getCurrentYear = (): number => new Date().getFullYear()

  const getPreviousYears = (yearsBefore: number): number[] => {
    const currentYear = getCurrentYear()
    const years = []

    for (let i = currentYear; i >= currentYear - yearsBefore; i--) {
      years.push(i)
    }

    return years
  }

  const getFutureYears = (yearsAfter: number): number[] => {
    const currentYear = getCurrentYear()
    const years = []

    for (let i = currentYear; i <= currentYear + yearsAfter; i++) {
      years.push(i)
    }

    return years
  }

  const getCurrentMonthName = (): string => DateTime.local().monthLong

  // Note: 'zone' defaults to EST for consistency with CRM dates.
  const convertDate = (date: Date | string, zone: string = 'America/New_York'): DateTime => {
    if (typeof date === 'string') {
      const formats = ['yyyy-MM-dd', 'dd/MM/yyyy', 'MM/dd/yyyy', 'yyyy/MM/dd']

      const matchedFormat = formats.find((format) => dayjs(date, format, true).isValid())

      return matchedFormat ? DateTime.fromJSDate(dayjs(date, matchedFormat).toDate(), { zone }) : DateTime.fromISO(date, { zone })
    }

    return DateTime.fromJSDate(date, { zone })
  }

  /*
   * Formats a date to a string
   * @param date - Date to format
   * @param format - Format to use. Format is expected to be a valid Intl.DateTimeFormatOptions. Default is DateTime.DATE_SHORT.
   * @returns Formatted date
   */
  const formatDate = (date: Date | string, format: Intl.DateTimeFormatOptions = DateTime.DATE_SHORT): string =>
    convertDate(date).toLocaleString(format)

  const dateToRelative = (date: Date | string): string | null => convertDate(date).toRelative()

  const isDateBeforeToday = (date: string): boolean => dayjs(date).isBefore(dayjs(), 'day')

  return {
    formatDate,
    dateToRelative,
    isDateBeforeToday,
    getPreviousYears,
    getFutureYears,
    months: getMonths(),
    currentDate: DateTime.local().toISODate(),
    currentMonth: getCurrentMonthName(),
    currentYear: getCurrentYear(),
    formattedDate: formatDate(new Date(), DateTime.DATETIME_SHORT)
  }
}

export default useDate
